import { createElement as rc } from 'react';
import { ActiveToggle } from '../formButton';
import { MenuDivider } from '../menuSelection';

const NON_WRAPPING_FORM_ELEMENTS = ['MultiPickerSensorGrid'];
const FORCE_TO_FORM_ELEMENTS = ['Slider', 'FormElementLayout'];

/** @typedef {import('react').ReactNode} ReactNode */

/**
 * @param {ReactNode} children
 * @returns {ReactNode | null}
 */
export function getFormElements(children) {
    if (!Array.isArray(children)) {
        // If there is only one child and it is a form element, return it.
        if (
            FORCE_TO_FORM_ELEMENTS.includes(children?.props?.hNode?.hNodeType) ||
            (children?.props?.hNode?.hNodeTypeGroup === 'formElement' &&
                !NON_WRAPPING_FORM_ELEMENTS.includes(children?.props?.hNode?.hNodeType))
        )
            return children;
        // If there is only one child and it is something else return null
        return null;
    }

    return children.filter(
        c =>
            FORCE_TO_FORM_ELEMENTS.includes(c?.props?.hNode?.hNodeType) ||
            (c?.props?.hNode?.hNodeTypeGroup === 'formElement' &&
                !NON_WRAPPING_FORM_ELEMENTS.includes(c?.props?.hNode?.hNodeType))
    );
}

/**
 * @param {ReactNode} children
 * @returns {ReactNode | null}
 */
export function getFormButtons(children) {
    if (!Array.isArray(children)) {
        // If there is only one child and it is a form button, return it.
        if (
            children?.props?.hNode?.hNodeBlockGroup === 'formButtons' ||
            children?.props?.hNode?.hNodeTypeGroup === 'formButton'
        ) {
            return children;
        }
        // If there is only one child and it is something else return null
        return null;
    }

    return children.filter(
        c => c?.props?.hNode?.hNodeBlockGroup === 'formButtons' || c?.props?.hNode?.hNodeTypeGroup === 'formButton'
    );
}

export function getOtherElements(children) {
    if (!Array.isArray(children)) {
        if (FORCE_TO_FORM_ELEMENTS.includes(children?.props?.hNode?.hNodeType)) return null;
        if (NON_WRAPPING_FORM_ELEMENTS.includes(children?.props?.hNode?.hNodeType)) return children;
        // If there is only one child and it is not a form button or form element, return it.
        if (
            children?.props?.hNode?.hNodeTypeGroup !== 'formElement' &&
            children?.props?.hNode?.hNodeBlockGroup !== 'formButtons' &&
            children?.props?.hNode?.hNodeTypeGroup !== 'formButton'
        )
            return children;
        // If there is only one child and it is a form button or form element return null
        return null;
    }

    return children.filter(c => {
        return (
            (c?.props?.hNode?.hNodeBlockGroup !== 'formButtons' &&
                c?.props?.hNode?.hNodeTypeGroup !== 'formButton' &&
                c?.props?.hNode?.hNodeTypeGroup !== 'formElement' &&
                !FORCE_TO_FORM_ELEMENTS.includes(c?.props?.hNode?.hNodeType)) ||
            NON_WRAPPING_FORM_ELEMENTS.includes(c?.props?.hNode?.hNodeType)
        );
    });
}

/**
 * @param {ReactNode[]} children
 * @param {boolean} allowRecordDeactivation
 * @returns {[ReactNode[] | ReactNode | null, ReactNode[]?, ReactNode?]}
 */
export function arrangeButtons(children, allowRecordDeactivation = false) {
    let menuButtons = null;
    let formButtons = getFormButtons(children);
    let hasActiveToggle = false;

    if (allowRecordDeactivation) {
        menuButtons = [];
        menuButtons.push(rc(ActiveToggle, {
            key: 'active-toggle',
            hNode: {
                id: 'item-active-toggle',
                title: 'Active',
                iconName: 'file',
                propertyName: 'active',
                formAction: 'update'
            }
        }));
        hasActiveToggle = true;
    }
    if (!Array.isArray(formButtons)) {
        return [formButtons, menuButtons, null];
    }

    menuButtons = menuButtons || [];
    let cancelButton = null;
    let i = formButtons.length;
    while (i--) {
        const button = formButtons[i];
        const hNode = button.props?.hNode || {};
        const formAction = hNode.formAction || hNode.forAction;
        
        if (!formAction) {
            continue;
        }

        if (formAction === 'cancel' && hNode.variant === 'tertiary') {
            cancelButton = button;
            formButtons.splice(i, 1);
        } else if (hNode.variant === 'tertiary') {
            menuButtons.push(button);
            formButtons.splice(i, 1);
        }
    }

    if (!menuButtons.length) {
        menuButtons = null;
    }
    else if (hasActiveToggle && menuButtons && menuButtons.length > 1) {
        // Add divider after the active toggle in menu buttons if it's not the only item
        //@ts-ignore
        menuButtons.splice(1, 0, rc(MenuDivider, { key: 'divider' }));
    }
    
    return [formButtons, menuButtons, cancelButton];
}